import { IOffice } from '@interfaces/office.interface';
import { IUser } from '@interfaces/user.interface';
import { rosterReportRequest } from '@shared/services/report.service';
import { userOptionRequest } from '@shared/services/user.service';
import { States } from '@shared/utils/enums';
import { exportJsonToCsv, getStateName, sortDateColumn, sortStringColumn } from '@shared/utils/functions';
import { Button, DatePicker, message, Select, Table } from 'antd';
import { Content } from 'antd/es/layout/layout';
import dayjs from 'dayjs';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { FaFileCsv } from 'react-icons/fa6';
import { MdOutlineQueryStats } from 'react-icons/md';

const { RangePicker } = DatePicker;

const columns = [
  { title: <span className='text-sm'>Agent Name</span>, width: 150, dataIndex: 'agentname', key: 'agentname', sorter: sortStringColumn('agentname') },
  { title: <span className='text-sm'>Email</span>, width: 150, dataIndex: 'email', key: 'email' },
  { title: <span className='text-sm'>Joined Date</span>, width: 80, dataIndex: 'hiredate', key: 'hiredate', sorter: sortDateColumn('hiredate'), render: (date: any) => date ? dayjs(date).format('MM/DD/YYYY') : '-'  },
  { title: <span className='text-sm'>Birthday</span>, width: 80, dataIndex: 'birthdate', key: 'birthdate', sorter: sortDateColumn('birthdate'), render: (date: any) => date ? dayjs(date).format('MM/DD/YYYY') : '-'  },
  { title: <span className='text-sm'>Status</span>, width: 80, dataIndex: 'active', key: 'active', sorter: (a: any, b: any) => Number(b.active) - Number(a.active), render: (value: boolean) => value ? 'Active' : 'Inactive' },
  { title: <span className='text-sm'>Last Associated Office</span>, width: 150, dataIndex: 'officename', key: 'officename', sorter: sortStringColumn('officename') },
  { title: <span className='text-sm'>Associated State</span>, width: 130, dataIndex: 'state', key: 'state', sorter: sortStringColumn('state'), render: (value: string) => getStateName(value) },
];

const Roster = () => {
  const [messageApi, contextHolder] = message.useMessage();
  
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<number[]>([1]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedOffices, setSelectedOffices] = useState<number[]>([]);
  const [offices, setOffices] = useState<IOffice[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<number[]>([]);
  const [agents, setAgents] = useState<IUser[]>([]);
  const [joinDates, setJoinDates] = useState<any[]>([]);
  const [birthMonths, setBirthMonths] = useState<number[]>([]);

  useMemo(() => {
    const fetchData = async () => {
      const options = await userOptionRequest();
      setOffices(options.response.offices.filter((office: any) => office.id !== 1).map((office: any) => ({ value: office.id, label: office.name })).sort((a: any, b: any) => a.label.localeCompare(b.label)));
      setAgents( options.response.users.map((user: any) => ({ value: user.id, label: user.fullName })).sort((a: any, b: any) => a.label.localeCompare(b.label)));

      setLoading(false);
    };

    fetchData();
  }, []); 

  const _OnRunReport = () => {
    setFetching(true);

    rosterReportRequest({ joinDates, birthMonths, agents: selectedAgents, offices: selectedOffices, states: selectedStates, status: selectedStatus })
      .then((res: any) => {
        setResults(res.response);
        setFetching(false);
      });
  };
  
    const _OnExportReport = () => {
      if(results.length > 0) {
        const csvData = results.map(({ __typename, agentname, email, hiredate, birthdate, active, ...rest }) => ({ agentname, email, hiredate: hiredate ? dayjs(hiredate).format('MM/DD/YYYY') : '', birthdate: birthdate ? dayjs(birthdate).format('MM/DD/YYYY') : '', active: active ? 'Active' : 'Inactive', ...rest }));
        exportJsonToCsv(csvData, `Agent_Roster_Report_${new Date().toISOString().replace(/[-:.TZ]/g, '')}`, ['Agent Name', 'Email', 'Joined Date', 'Birthday', 'Status', 'Last Associated Office', 'Associated State']);
        messageApi.open({ type: 'success', content: 'Report data exported correctly' });
      } else {
        messageApi.open({ type: 'error', content: 'There are no records to export' });
      }
    };
  
    return (
      <>
        {contextHolder}
        <Content className='px-4 h-[97%]'>
          <div className='w-full grid grid-cols-12 grid-rows-2 gap-4 mb-4'>
            <div className='col-span-5 flex items-center'>
              <div className='mr-2'>Agent: </div>
              <Select
                mode='multiple'
                showSearch
                placeholder='Select an agent'
                optionFilterProp='label'
                className='w-full'
                loading={loading}
                options={agents}
                value={selectedAgents}
                onChange={setSelectedAgents}
              />
            </div>
            <div className='col-span-5 flex items-center'>
              <div className='mr-2'>Office: </div>
              <Select
                mode='multiple'
                showSearch
                placeholder='Select an office'
                optionFilterProp='label'
                className='w-full mr-8'
                loading={loading}
                options={offices}
                value={selectedOffices}
                onChange={setSelectedOffices}
              />
            </div>
            <div className='col-span-2 flex items-center justify-end'>
              <Button className='rounded-xl' onClick={_OnRunReport} icon={<MdOutlineQueryStats size={20} className='mt-1' />}>Run Report</Button>
              <Button className='rounded-xl ml-2' onClick={_OnExportReport} icon={<FaFileCsv size={16} className='mt-1' />}>Export</Button>
            </div>
            <div className='col-span-3 flex items-center'>
              <div className='whitespace-nowrap mr-2'>Joined Date: </div>
              <RangePicker
                format='MM/DD/YYYY'
                className='w-full'
                placeholder={['Start Date', 'End Date']} 
                onChange={(dates) => { dates ? setJoinDates([dayjs(dates[0]).startOf('day').toDate(), dayjs(dates[1]).endOf('day').toDate()]) : setJoinDates([]) }}
              />
            </div>
            <div className='col-span-3 flex items-center justify-start'>
              <div className='whitespace-nowrap mr-2'>Birthday: </div>
              <Select
                mode='multiple'
                showSearch
                optionFilterProp='label'
                className='w-full'
                value={birthMonths}
                maxTagCount='responsive'
                onChange={setBirthMonths}
                options={moment.months().map((month, index) => ({ label: month, value: index + 1 }))}
              />
            </div>
            <div className='col-span-2 flex items-center justify-start'>
              <div className='whitespace-nowrap mr-2'>Active Status: </div>
              <Select
                mode='multiple'
                showSearch
                optionFilterProp='label'
                className='w-full'
                maxTagCount='responsive'
                value={selectedStatus}
                onChange={setSelectedStatus}
                options={[{ value: 1, label: 'Active' }, { value: 2, label: 'Inactive' }]}
              />
            </div>
            <div className='col-span-2 flex items-center justify-start'>
              <div className='mr-2'>State: </div>
              <Select
                mode='multiple'
                showSearch
                placeholder='Select a state'
                optionFilterProp='label'
                className='w-full mr-8'
                maxTagCount='responsive'
                options={Object.values(States).map((val) => ({ value: val, label: getStateName(val) }))}
                value={selectedStates}
                onChange={setSelectedStates}
              />
            </div>
          </div>
          <div className='h-[calc(100vh-280px)]'>
            <Table
              columns={columns}
              loading={fetching}
              size='small'
              pagination={{ 
                pageSizeOptions: ['25', '50', '100'], 
                defaultPageSize: 50,
                total: results.length,
                showTotal: (total) => `${total} Results`,
              }}
              scroll={{ x: 'max-content', y: 'calc(100vh - 380px)' }}
              rowClassName={() => 'font-row-sm'}
              dataSource={results}
            />
          </div>
        </Content>
      </>
  );
};

export default Roster;